import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { apryseEventSliceCurrentPage, apryseEventSliceCurrentScale } from '../../../Redux/ApryseEventSlice';
import { ApryseSDK } from '../../../pdf/ApryseSDK';

// ─── Physics ──────────────────────────────────────────────────────────────────

// At zoom = 1 in Apryse, 1 PDF point = 1 CSS pixel. 72 pt = 1 inch = 25.4 mm.
const PX_PER_MM_AT_ZOOM_1 = 72 / 25.4; // ≈ 2.835

// Fixed tick grid: minor every 2 mm, mid every 5 mm, major every 10 mm (= 1 cm).
const TICK_MINOR_MM = 2;
const TICK_MID_MM   = 5;
const TICK_MAJOR_MM = 10;

// ─── Design tokens ───────────────────────────────────────────────────────────

const RULER_BG   = '#f0f0f0';
const BORDER     = '#9a9a9a';
const TICK_MAJOR = '#3c3c3c';
const TICK_MID   = '#7a7a7a';
const TICK_MINOR = '#b8b8b8';
const LABEL_CLR  = '#3c3c3c';
const LABEL_FONT = '800 8.5px "Segoe UI", system-ui, sans-serif';

// ─── Props ───────────────────────────────────────────────────────────────────

export type RulerOverlayProps = {
    viewerReady:  boolean;
    containerRef: React.RefObject<HTMLDivElement>;
    rulerWidth?:  number;
    rulerHeight?: number;
    showTopRuler?:  boolean;
    showLeftRuler?: boolean;
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const snapZero = (n: number, step: number) => Math.abs(n % step) < 0.001;

/**
 * Read scroll position AND the position of the current PDF page within the
 * scroll container so that the ruler's zero point aligns with the page origin.
 *
 * Strategy
 * ─────────
 * Apryse renders pages as <div id="pageContainerN" …> inside the scroll
 * element.  getBoundingClientRect() on both the scroll container and the page
 * gives their positions in the iframe viewport.  Converting to scroll-content
 * coordinates (+ scrollTop/Left) yields the fixed offset of the page within
 * the scrollable area.  Subtracting that offset from the live scroll position
 * gives an "effective scroll" where 0 = top-left of the current PDF page.
 *
 * When the page changes (e.g. portrait → landscape), the zero point moves to
 * the top-left corner of the new current page automatically.
 */
function readScrollState(
    docViewer: { getScrollViewElement: () => Element },
    currentPage: number,
): {
    /** effectiveScrollTop  = scrollTop  − pageOffsetTop  (0 = page origin) */
    scrollTop: number;
    /** effectiveScrollLeft = scrollLeft − pageOffsetLeft (0 = page origin) */
    scrollLeft: number;
} {
    const scrollEl  = docViewer.getScrollViewElement() as HTMLElement | null;
    const rawTop    = scrollEl?.scrollTop  ?? 0;
    const rawLeft   = scrollEl?.scrollLeft ?? 0;

    let pageOffsetTop  = 0;
    let pageOffsetLeft = 0;

    try {
        if (scrollEl) {
            const iframeDoc = scrollEl.ownerDocument;
            // Apryse names the current page container "pageContainerN".
            // Fall back to page 1 container, then data attribute, then first .pageContainer.
            const pageEl = (
                iframeDoc.getElementById(`pageContainer${currentPage}`) ??
                iframeDoc.getElementById('pageContainer1') ??
                iframeDoc.querySelector(`[data-page-number="${currentPage}"]`) ??
                iframeDoc.querySelector('[data-page-number="1"]') ??
                iframeDoc.querySelector('.pageContainer')
            ) as HTMLElement | null;

            if (pageEl) {
                const scrollRect = scrollEl.getBoundingClientRect();
                const pageRect   = pageEl.getBoundingClientRect();
                // Convert iframe-viewport coords → scroll-content coords
                pageOffsetTop  = pageRect.top  - scrollRect.top  + rawTop;
                pageOffsetLeft = pageRect.left - scrollRect.left + rawLeft;
            }
        }
    } catch {
        // Cross-origin or DOM access error – fall back to scroll-container origin.
    }

    return {
        scrollTop:  rawTop  - pageOffsetTop,
        scrollLeft: rawLeft - pageOffsetLeft,
    };
}

// ─── Canvas drawing (size set imperatively, bypasses React style) ─────────────

interface DrawInfo {
    zoom: number; scrollTop: number; scrollLeft: number;
    rulerW: number; rulerH: number;
    /** Exact CSS pixel width  the canvas should occupy. */
    cssW: number;
    /** Exact CSS pixel height the canvas should occupy. */
    cssH: number;
}

function applyCanvasSize(canvas: HTMLCanvasElement, cssW: number, cssH: number): boolean {
    if (cssW < 1 || cssH < 1) return false;
    const dpr = window.devicePixelRatio || 1;
    canvas.width  = Math.round(cssW * dpr);
    canvas.height = Math.round(cssH * dpr);
    // Set CSS display size imperatively (no React style prop for width/height).
    canvas.style.width  = `${cssW}px`;
    canvas.style.height = `${cssH}px`;
    return true;
}

function drawVertical(canvas: HTMLCanvasElement, d: DrawInfo): void {
    if (!applyCanvasSize(canvas, d.cssW, d.cssH)) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx  = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = RULER_BG;
    ctx.fillRect(0, 0, d.cssW, d.cssH);

    // Right border (inner edge facing document)
    ctx.strokeStyle = BORDER; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(d.cssW - 0.5, 0);
    ctx.lineTo(d.cssW - 0.5, d.cssH);
    ctx.stroke();

    // Ticks: fixed 1 mm grid, labels at every 10 mm (= 1 cm).
    const pxMm = d.zoom * PX_PER_MM_AT_ZOOM_1;

    const mmMin = Math.floor(d.scrollTop / pxMm / TICK_MINOR_MM) * TICK_MINOR_MM - TICK_MAJOR_MM;
    const mmMax = Math.ceil((d.scrollTop + d.cssH) / pxMm / TICK_MINOR_MM) * TICK_MINOR_MM + TICK_MAJOR_MM;

    ctx.font = LABEL_FONT; ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
    const borderX = d.cssW - 1.5; // right border x-position

    for (let mm = mmMin; mm <= mmMax; mm += TICK_MINOR_MM) {
        const y = mm * pxMm - d.scrollTop;
        if (y < -2 || y > d.cssH + 2) continue;

        const isMaj = snapZero(mm, TICK_MAJOR_MM);
        const isMid = !isMaj && snapZero(mm, TICK_MID_MM);

        ctx.strokeStyle = isMaj ? TICK_MAJOR : isMid ? TICK_MID : TICK_MINOR;
        ctx.lineWidth   = isMaj ? 1.2 : 1;
        const len = isMaj ? d.rulerW * 0.58 : isMid ? d.rulerW * 0.36 : d.rulerW * 0.20;

        const fy = Math.round(y) + 0.5;
        ctx.beginPath(); ctx.moveTo(borderX - len, fy); ctx.lineTo(borderX, fy); ctx.stroke();

        // Label at every full cm (= every 10 mm). Show value in cm.
        // Guard: centre of the rotated text (≈ half font-size = 4 px) must
        // be inside the canvas so it is not clipped.
        if (isMaj && y > 4 && y < d.cssH - 4) {
            ctx.save();
            ctx.fillStyle = LABEL_CLR;
            ctx.translate(d.rulerW * 0.34, y);
            ctx.rotate(-Math.PI / 2);
            ctx.fillText(String(Math.round(mm / 10)), 0, 0);
            ctx.restore();
        }
    }
}

function drawHorizontal(canvas: HTMLCanvasElement, d: DrawInfo): void {
    if (!applyCanvasSize(canvas, d.cssW, d.cssH)) return;

    const dpr = window.devicePixelRatio || 1;
    const ctx  = canvas.getContext('2d');
    if (!ctx) return;

    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = RULER_BG;
    ctx.fillRect(0, 0, d.cssW, d.cssH);

    // Bottom border
    ctx.strokeStyle = BORDER; ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(0,     d.cssH - 0.5);
    ctx.lineTo(d.cssW, d.cssH - 0.5);
    ctx.stroke();

    // Ticks: fixed 1 mm grid, labels at every 10 mm (= 1 cm).
    const pxMm = d.zoom * PX_PER_MM_AT_ZOOM_1;

    const mmMin = Math.floor(d.scrollLeft / pxMm / TICK_MINOR_MM) * TICK_MINOR_MM - TICK_MAJOR_MM;
    const mmMax = Math.ceil((d.scrollLeft + d.cssW) / pxMm / TICK_MINOR_MM) * TICK_MINOR_MM + TICK_MAJOR_MM;

    ctx.font = LABEL_FONT; ctx.textBaseline = 'top'; ctx.textAlign = 'left';
    const borderY = d.cssH - 1.5;

    for (let mm = mmMin; mm <= mmMax; mm += TICK_MINOR_MM) {
        const x = mm * pxMm - d.scrollLeft;
        if (x < -2 || x > d.cssW + 2) continue;

        const isMaj = snapZero(mm, TICK_MAJOR_MM);
        const isMid = !isMaj && snapZero(mm, TICK_MID_MM);

        ctx.strokeStyle = isMaj ? TICK_MAJOR : isMid ? TICK_MID : TICK_MINOR;
        ctx.lineWidth   = isMaj ? 1.2 : 1;
        const len = isMaj ? d.rulerH * 0.58 : isMid ? d.rulerH * 0.36 : d.rulerH * 0.20;

        const fx = Math.round(x) + 0.5;
        ctx.beginPath(); ctx.moveTo(fx, borderY - len); ctx.lineTo(fx, borderY); ctx.stroke();

        // Label at every full cm (= every 10 mm). Show value in cm.
        // Clamp label x so "0" is never drawn off the left edge of the canvas.
        if (isMaj && x > -4 && x < d.cssW - 2) {
            ctx.fillStyle = LABEL_CLR;
            ctx.fillText(String(Math.round(mm / 10)), Math.max(2, fx + 2), 2);
        }
    }
}

// ─── Component ────────────────────────────────────────────────────────────────

const RulerOverlay: React.FC<RulerOverlayProps> = ({
    viewerReady,
    containerRef,
    rulerWidth    = 30,
    rulerHeight   = 30,
    showTopRuler  = true,
    showLeftRuler = true,
}) => {
    const vCanvasRef = useRef<HTMLCanvasElement>(null);
    const hCanvasRef = useRef<HTMLCanvasElement>(null);

    // Zoom and page from Redux act as redraw triggers; actual values are read live.
    const zoom        = useSelector(apryseEventSliceCurrentScale);
    const currentPage = useSelector(apryseEventSliceCurrentPage);

    // Stable ref – always holds the latest closure so listeners never go stale.
    const redrawRef = useRef<() => void>(() => undefined);

    redrawRef.current = () => {
        const docViewer  = ApryseSDK.documentViewer;
        const container  = containerRef.current;
        if (!docViewer || !container) return;

        // Container size (the full viewer area).
        const { width: cw, height: ch } = container.getBoundingClientRect();
        if (cw < 1 || ch < 1) return;

        // Scroll position from the Apryse scroll element, anchored to the current page.
        const { scrollTop, scrollLeft } = readScrollState(docViewer, currentPage);

        const base: Omit<DrawInfo, 'cssW' | 'cssH'> = {
            zoom:      docViewer.getZoomLevel(),
            scrollTop,
            scrollLeft,
            rulerW: rulerWidth,
            rulerH: rulerHeight,
        };

        // Canvas sizes: span the full padding area (viewer is in the content
        // area after the padding, so canvases never overlap the viewer or scrollbars).
        if (showLeftRuler && vCanvasRef.current) {
            drawVertical(vCanvasRef.current, {
                ...base,
                cssW: rulerWidth,
                cssH: ch - (showTopRuler ? rulerHeight : 0),
            });
        }
        if (showTopRuler && hCanvasRef.current) {
            drawHorizontal(hCanvasRef.current, {
                ...base,
                cssW: cw - (showLeftRuler ? rulerWidth : 0),
                cssH: rulerHeight,
            });
        }
    };

    // ── ResizeObserver: redraw when the container changes size ────────────
    useEffect(() => {
        if (!viewerReady) return;
        const el = containerRef.current;
        if (!el) return;

        const ro = new ResizeObserver(() => redrawRef.current());
        ro.observe(el);
        redrawRef.current(); // initial draw
        return () => ro.disconnect();
    }, [containerRef, viewerReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Scroll + documentLoaded listeners ─────────────────────────────────
    useEffect(() => {
        if (!viewerReady) return;
        const docViewer = ApryseSDK.documentViewer;
        if (!docViewer) return;

        let scrollEl: Element | null = null;
        const onScroll = () => redrawRef.current();
        const onResize = () => redrawRef.current();

        const attachScroll = () => {
            scrollEl?.removeEventListener('scroll', onScroll);
            scrollEl = docViewer.getScrollViewElement() ?? null;
            scrollEl?.addEventListener('scroll', onScroll, { passive: true });
        };

        const onDocumentLoaded = () => { attachScroll(); redrawRef.current(); };

        attachScroll();
        docViewer.addEventListener?.('documentLoaded', onDocumentLoaded);
        window.addEventListener('resize', onResize);

        return () => {
            scrollEl?.removeEventListener('scroll', onScroll);
            docViewer.removeEventListener?.('documentLoaded', onDocumentLoaded);
            window.removeEventListener('resize', onResize);
        };
    }, [viewerReady]); // eslint-disable-line react-hooks/exhaustive-deps

    // ── Redraw on zoom or page change ─────────────────────────────────────
    // When the page changes (e.g. portrait → landscape), the zero point must
    // shift to the top-left corner of the new current page.
    useEffect(() => {
        if (viewerReady) redrawRef.current();
    }, [zoom, currentPage, viewerReady]);

    if (!viewerReady) return null;

    return (
        <>
            {/* Corner square */}
            {showTopRuler && showLeftRuler && (
                <div style={{
                    position: 'absolute', top: 0, left: 0,
                    width: rulerWidth, height: rulerHeight,
                    backgroundColor: RULER_BG,
                    borderRight:  `1px solid ${BORDER}`,
                    borderBottom: `1px solid ${BORDER}`,
                    boxSizing: 'border-box',
                    zIndex: 11, pointerEvents: 'none',
                }} />
            )}

            {/* Horizontal (top) ruler – width/height set imperatively in drawHorizontal */}
            {showTopRuler && (
                <canvas ref={hCanvasRef} style={{
                    position: 'absolute',
                    top:  0,
                    left: showLeftRuler ? rulerWidth : 0,
                    // NO width / height here – set by applyCanvasSize() each draw
                    zIndex: 10, pointerEvents: 'none', display: 'block',
                }} />
            )}

            {/* Vertical (left) ruler */}
            {showLeftRuler && (
                <canvas ref={vCanvasRef} style={{
                    position: 'absolute',
                    top:  showTopRuler ? rulerHeight : 0,
                    left: 0,
                    // NO width / height here – set by applyCanvasSize() each draw
                    zIndex: 10, pointerEvents: 'none', display: 'block',
                }} />
            )}
        </>
    );
};

export default RulerOverlay;
